
export namespace ColorUtils {
  const HEX_EXP = /^#[0-9a-f]{3,6}$/i;

  // 3 - 16 comma separated numbers
  const CSV_EXP = /(\s*[\d\.]+\s*,){2,15}\s*[\d\.]+\s*/g;

  class ColorModel {
    '1': [number, number, number, number];
    '255': [number, number, number, number];
  }

  export function is255(channels: any[]): boolean {
    for (let i = 0; i < 3; i++) {
      const channelValue = `${channels[i]}`;
      if (parseFloat(channelValue) > 1) {
        return true;
      }
    }
    return false;
  }

  export function isLight(outerRGB: [number, number, number], innerRGB?: [number, number, number]) {
    if (!innerRGB) {
      innerRGB = [...outerRGB] as [number, number, number];
    }

    // Convert RGB to HSP (Hue, Saturation, "Perceived Lightness"), a reliable metric for 'lightness'
    // Support outer and inner to account for gradients
    const outerHSP = rgbToHSP(outerRGB);
    const innerHSP = rgbToHSP(innerRGB);
    const hsp = (outerHSP + innerHSP) / 2;

    // 60% or below is considered 'dark'
    // 255 * 0.55 = 140.25
    return (hsp > 140.25);
  }

  export function getComputedColor(input: string): string {
    const el = document.createElement('div');
    el.style.backgroundColor = input;
    document.documentElement.appendChild(el);
    const computedColor = window.getComputedStyle(el).backgroundColor.replace(/\s/g, '');
    window.document.documentElement.removeChild(el);
    return computedColor;
  }

  export function convertChannels(input: any[]): any[] {
    const channels = input.slice(0, 4);

    if (channels.length === 3) {
      channels.push(1);
    }

    for (let i = 0; i < 4; i++) {
      const channelValue = `${channels[i]}`;
      channels[i] = parseFloat(channelValue);

      if (channels[i] < 0) {
        channels[i] = 0;
      }

      if (channels[i] > 255) {
        channels[i] = 255;
      }
    }

    if (channels[3] > 1) {
      channels[3] = 1;
    }

    return channels;
  }

  // Convert a single color
  export function convert(input: any): any[] {
    let color: any[];

    if (Array.isArray(input)) {
      color = convertChannels(input);
    } else if (typeof input === 'string') {
      let csvMatch = input.match(CSV_EXP);

      if (csvMatch) {
        color = convertChannels(csvMatch[0].split(','));
      } else {
        // See if the browser can compute the color

        const computedColor = getComputedColor(input);
        csvMatch = computedColor.match(CSV_EXP);

        if (csvMatch) {
          color = convertChannels(csvMatch[0].split(','));
        }
      }
    }

    return color;
  }

  export function getColor(input): ColorModel {
    // Convert input to 4 length array
    const color = convert(input);

    if (!color) {
      throw new TypeError('Invalid color input.');
    }

    // Scale color
    const scaledColor = scaleColor(color);

    const color1: any = is255(color) ? scaledColor : color;
    const color255: any = is255(color) ? color : scaledColor;

    return {
      '1': color1,
      '255': color255
    };
  }

  export function getColors(input: string | any[]): ColorModel[] {
    let colors = [];

    if (typeof input === 'string') {
      const csvMatch = input.match(CSV_EXP);

      // Try to extract numbers from the string
      if (csvMatch) {
        input = csvMatch.join(',');
      }

      // Split it on commas
      input = input.split(',');
    }

    if (Array.isArray(input)) {
      const nestedArrays = Array.isArray(input[0]) && Array.isArray(input[1]);

      if (nestedArrays) {
        // Format is good as is
        colors = input;
      } else if (input.length <= 2) {
        // Arrays of 2 items or less can be CSS color name(s)
        colors = input.length === 1 ? input.concat(input) : input;
      } else {
        // Should be a set of numbers between 3 - 16
        const half = Math.floor(input.length / 2);
        // Greater than 4 is assumed to be two color values
        if (input.length > 4) {
          colors = [input.slice(0, half), input.slice(half)];
        } else {
          // Single color input, apply to both colors
          colors = [input, input];
        }
      }
    }

    return colors.map(color => getColor(color));
  }

  export function scaleColor(channels: any[]): any[] {
    const _is255 = is255(channels);
    const scaledChannels = [];

    // Don't scale alpha channel
    for (let i = 0; i < 3; i++) {
      let scaledChannel = _is255 ? channels[i] / 255 : channels[i] * 255;

      if (_is255) {
        scaledChannel = Math.round(scaledChannel * 100) / 100;
      } else {
        scaledChannel = Math.round(scaledChannel);
      }

      scaledChannels.push(scaledChannel);
    }

    if (channels.length === 4) {
      scaledChannels.push(channels[3]);
    }

    return scaledChannels;
  }

  export function hexToRgba(
    hex: string,
    asDecimal = false
  ): [number, number, number, number] {
    // normalize hex
    hex = hex
      .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) =>
        [r, r, g, g, b, b].join('')
      ) // #fff -> #ffffff
      .replace(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i, (m, r, g, b) =>
        [r, g, b, 'ff'].join('')
      ); // #ffffff -> #ffffffff

    const regex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
      hex
    ); // #ffffffff
    const red = parseInt(regex[1], 16);
    const green = parseInt(regex[2], 16);
    const blue = parseInt(regex[3], 16);
    const alpha = parseInt(regex[4], 16) / 255; // important: 0...1

    if (asDecimal) {
      const rgb = rgbToDecimal([red, green, blue]);
      return [rgb[0], rgb[1], rgb[2], alpha];
    } else {
      return [red, green, blue, alpha];
    }
  }

  export function hexToRgb(
    hex: string,
    asDecimal = false
  ): [number, number, number] {
    const rgba = hexToRgba(hex, asDecimal);
    return [rgba[0], rgba[1], rgba[2]];
  }

  export function rgbToHex(rgb: [number, number, number], isDecimal = false) {
    if (isDecimal) {
      return toHex(rgbFromDecimal(rgb));
    } else {
      return toHex(rgb);
    }
  }

  export function rgbToHSP(
    rgb: [number, number, number]
  ) {
    const [r, g, b] = rgb;

    // HSP equation
    // http://alienryderflex.com/hsp.html
    const hsp = Math.sqrt(
      0.299 * (r * r) +
      0.587 * (g * g) +
      0.114 * (b * b)
    );
    
    return hsp;
  }

  export function rgbaToHex(
    rgba: [number, number, number, number],
    isDecimal = false
  ) {
    const red = rgba[0];
    const green = rgba[1];
    const blue = rgba[2];
    const alpha = rgba[3] * 255; // important: 0...1 -> 0...255
    if (isDecimal) {
      return toHex([...rgbFromDecimal([red, green, blue]), alpha]);
    } else {
      return toHex([red, green, blue, alpha]);
    }
  }

  export function rgbToDecimal(
    rgb: [number, number, number]
  ): [number, number, number] {
    const result = rgb.map(value => {
      return value / 255;
    }) as [number, number, number];

    return result;
  }

  export function rgbFromDecimal(
    rgb: [number, number, number]
  ): [number, number, number] {
    const result = rgb.map(value => {
      return Math.round(value * 255);
    }) as [number, number, number];

    return result;
  }

  function toHex(values: number[]): string {
    const hex = values
      .map(value => {
        const hexString = value.toString(16);
        return hexString.length % 2 ? `0${hexString}` : hexString;
      })
      .join('');

    return `#${hex}`;
  }
}
